import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
    AlertCircle,
    Loader2,
    UserCheck,
    TrendingUp,
    Clock,
    CalendarDays,
    Users,
    UserX,
    X,
    Gift,
    UserPlus,
    Settings,
    PlusCircle,
    Award,
    ClipboardCheck,
    Palmtree,
    Radio,
    Check,
    Search,
    RefreshCw,
    RotateCcw,
    Calendar,
    Eye,
    EyeOff,
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight
} from 'lucide-react';
import confetti from 'canvas-confetti';
import CircularStatsPanel from './dashboard/CircularStatsPanel';
import QualityDashboardWidget from './QualityDashboardWidget';
import PayrollStatusCard from './dashboard/PayrollStatusCard';
import PendingApprovalWidget from './dashboard/PendingApprovalWidget';
import MyLeaveSummaryWidget from './dashboard/MyLeaveSummaryWidget';
import TeamStatusWidget from './dashboard/TeamStatusWidget';
import LeaveEntitlementWidget from './dashboard/LeaveEntitlementWidget';
import AnnouncementsWidget from './dashboard/AnnouncementsWidget';
import DashboardSpecialDayBanner from './dashboard/DashboardSpecialDayBanner';
import DeviceReachabilityCard from './dashboard/DeviceReachabilityCard';
import DashboardSortableGrid, {
    DASHBOARD_PANEL_GRID_CLASS,
    getDefaultPanelSize,
    panelSizeToClass
} from './dashboard/DashboardSortableGrid';
import { apiClient } from '../utils/apiClient';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';

const getLocalTodayStr = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const getLocalYesterdayStr = () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const LIST_PANEL_KEYS = new Set([
    'insideEmployees', 'lateArrivalsEmployees', 'onLeaveEmployees', 'absentEmployees',
    'outsideEmployees', 'upcomingAnniversariesEmployees', 'newStartersEmployees',
    'todayBirthdaysEmployees', 'upcomingBirthdaysEmployees'
]);

// Ortak Modal Bileşeni (PlayerListModal)
const PlayerListModal = ({ isOpen, onClose, title, employees, isLoading, error, renderItemExtra }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(50);
    const [nameQuery, setNameQuery] = useState('');
    const [deptQuery, setDeptQuery] = useState('');
    const [positionQuery, setPositionQuery] = useState('');

    const filteredEmployees = useMemo(() => {
        const list = employees || [];
        const nameQ = nameQuery.trim().toLocaleLowerCase('tr-TR');
        const deptQ = deptQuery.trim().toLocaleLowerCase('tr-TR');
        const posQ = positionQuery.trim().toLocaleLowerCase('tr-TR');
        if (!nameQ && !deptQ && !posQ) return list;

        return list.filter(emp => {
            if (nameQ && !(emp.fullName || '').toLocaleLowerCase('tr-TR').includes(nameQ)) return false;
            if (deptQ && !(emp.departmentName || '').toLocaleLowerCase('tr-TR').includes(deptQ)) return false;
            if (posQ && !(emp.positionName || '').toLocaleLowerCase('tr-TR').includes(posQ)) return false;
            return true;
        });
    }, [employees, nameQuery, deptQuery, positionQuery]);

    const totalPages = Math.max(1, Math.ceil(filteredEmployees.length / itemsPerPage));
    const safePage = Math.min(currentPage, totalPages);
    const startIndex = (safePage - 1) * itemsPerPage;
    const currentEmployees = useMemo(
        () => filteredEmployees.slice(startIndex, startIndex + itemsPerPage),
        [filteredEmployees, startIndex, itemsPerPage]
    );

    useEffect(() => {
        if (isOpen) {
            setCurrentPage(1);
            setNameQuery('');
            setDeptQuery('');
            setPositionQuery('');
        }
    }, [isOpen]);

    useEffect(() => {
        setCurrentPage(1);
    }, [nameQuery, deptQuery, positionQuery, itemsPerPage]);

    useEffect(() => {
        if (currentPage > totalPages) setCurrentPage(totalPages);
    }, [currentPage, totalPages]);

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4 overflow-hidden"
            onClick={onClose}
        >
            <div
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-5xl h-[90vh] max-h-[90vh] border border-gray-200 dark:border-gray-700 flex flex-col overflow-hidden relative"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="shrink-0 flex justify-between items-start px-6 py-4 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80">
                    <div>
                        <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100">{title} Listesi</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {filteredEmployees.length === (employees || []).length
                                ? `Toplam ${(employees || []).length.toLocaleString('tr-TR')} personel`
                                : `Filtrelenen: ${filteredEmployees.length.toLocaleString('tr-TR')} / ${(employees || []).length.toLocaleString('tr-TR')} personel`}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="shrink-0 px-4 py-3 border-b border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <label className="relative block">
                        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                        <input
                            type="search"
                            value={nameQuery}
                            onChange={(e) => setNameQuery(e.target.value)}
                            placeholder="Personel ara…"
                            className="w-full pl-8 pr-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/40 text-gray-800 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400"
                        />
                    </label>
                    <label className="relative block">
                        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                        <input
                            type="search"
                            value={deptQuery}
                            onChange={(e) => setDeptQuery(e.target.value)}
                            placeholder="Departman ara…"
                            className="w-full pl-8 pr-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/40 text-gray-800 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400"
                        />
                    </label>
                    <label className="relative block">
                        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                        <input
                            type="search"
                            value={positionQuery}
                            onChange={(e) => setPositionQuery(e.target.value)}
                            placeholder="Pozisyon ara…"
                            className="w-full pl-8 pr-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/40 text-gray-800 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400"
                        />
                    </label>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3 bg-gray-50/40 dark:bg-gray-900/20">
                    {isLoading ? (
                        <div className="flex justify-center items-center py-12 text-gray-700 dark:text-gray-300">
                            <Loader2 className="animate-spin mr-2" /> Yükleniyor...
                        </div>
                    ) : error ? (
                        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative dark:bg-red-900 dark:text-red-200" role="alert">
                            <AlertCircle className="inline mr-2" size={18} /> {error}
                        </div>
                    ) : filteredEmployees.length === 0 ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center">
                            {(employees || []).length === 0 ? 'Bu kategoride personel bulunmamaktadır.' : 'Arama kriterlerine uygun personel yok.'}
                        </p>
                    ) : (
                        <ul className="space-y-2">
                            {currentEmployees.map(emp => (
                                <li key={emp.id} className="flex flex-wrap items-center gap-x-2 gap-y-1 p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 text-gray-800 dark:text-gray-200 shadow-sm">
                                    <UserCheck size={18} className="mr-1 text-blue-500 shrink-0" />
                                    <span className="font-semibold text-sm">{emp.fullName}</span>
                                    {emp.departmentName && <span className="text-gray-500 dark:text-gray-400 text-xs">({emp.departmentName})</span>}
                                    {emp.positionName && emp.positionName !== '-' && <span className="text-gray-400 dark:text-gray-500 text-xs">• {emp.positionName}</span>}
                                    {renderItemExtra && renderItemExtra(emp)}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {!isLoading && !error && filteredEmployees.length > 0 && (
                    <div className="shrink-0 flex flex-wrap justify-between items-center gap-3 px-5 py-3 border-t border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
                        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                            <span>
                                Gösterilen:{' '}
                                <strong className="text-gray-800 dark:text-gray-200">
                                    {(startIndex + 1).toLocaleString('tr-TR')}–{Math.min(startIndex + itemsPerPage, filteredEmployees.length).toLocaleString('tr-TR')}
                                </strong>{' '}
                                / {filteredEmployees.length.toLocaleString('tr-TR')}
                            </span>
                            <select
                                value={itemsPerPage}
                                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                                className="ml-2 px-2 py-1 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-xs font-medium focus:outline-none"
                                title="Sayfa başına kayıt sayısı"
                            >
                                <option value={25}>25 / sayfa</option>
                                <option value={50}>50 / sayfa</option>
                                <option value={100}>100 / sayfa</option>
                            </select>
                        </div>

                        <div className="flex items-center space-x-1.5">
                            <button
                                type="button"
                                onClick={() => setCurrentPage(1)}
                                disabled={safePage === 1}
                                className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 transition-colors"
                                title="İlk Sayfa"
                            >
                                <ChevronsLeft size={15} />
                            </button>
                            <button
                                type="button"
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                disabled={safePage === 1}
                                className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-40 text-xs font-semibold inline-flex items-center gap-1 transition-colors"
                            >
                                <ChevronLeft size={14} /> Önceki
                            </button>
                            <span className="px-2.5 text-gray-700 dark:text-gray-300 text-xs font-semibold tabular-nums">
                                Sayfa {safePage} / {totalPages}
                            </span>
                            <button
                                type="button"
                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                disabled={safePage === totalPages}
                                className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-40 text-xs font-semibold inline-flex items-center gap-1 transition-colors"
                            >
                                Sonraki <ChevronRight size={14} />
                            </button>
                            <button
                                type="button"
                                onClick={() => setCurrentPage(totalPages)}
                                disabled={safePage === totalPages}
                                className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 transition-colors"
                                title="Son Sayfa"
                            >
                                <ChevronsRight size={15} />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// Panel Ekleme ve Yönetme Modalı (Çoklu seçim destekli, her eklemede kapanmaz)
const AddPanelModal = ({
    isOpen,
    onClose,
    allPanelConfigs,
    visiblePanelKeys,
    onTogglePanel,
    onAddAllPanels,
    dashboardStats,
    hideEmptyLists,
    onToggleHideEmptyLists
}) => {
    if (!isOpen) return null;

    const activeSet = new Set(visiblePanelKeys || []);
    const missingCount = allPanelConfigs.filter(p => !activeSet.has(p.key)).length;

    return (
        <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-[90] p-4"
            onClick={onClose}
        >
            <div
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg border border-gray-200 dark:border-gray-700 max-h-[85vh] flex flex-col overflow-hidden relative"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Üst Başlık */}
                <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100 dark:border-gray-700 bg-gray-50/70 dark:bg-gray-800/80 shrink-0">
                    <div>
                        <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100">Anasayfa Panellerini Yönet</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            İstediğiniz panelleri tek tek ekleyip çıkarabilirsiniz. Pencere siz kapatana kadar açık kalır.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-1.5 rounded-full text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Boş Liste Görünürlük Ayarı ve Tümünü Ekle */}
                <div className="px-6 py-3 bg-indigo-50/60 dark:bg-indigo-950/30 border-b border-indigo-100 dark:border-indigo-900/50 flex flex-wrap items-center justify-between gap-2 shrink-0">
                    <button
                        type="button"
                        onClick={onToggleHideEmptyLists}
                        className="inline-flex items-center gap-2 text-xs font-medium text-gray-700 dark:text-gray-200 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                    >
                        {hideEmptyLists ? (
                            <EyeOff size={15} className="text-amber-500 shrink-0" />
                        ) : (
                            <Eye size={15} className="text-emerald-500 shrink-0" />
                        )}
                        <span>
                            Boş personel listeleri:{' '}
                            <strong className={hideEmptyLists ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'}>
                                {hideEmptyLists ? 'Normal görünümde gizlenir' : 'Her zaman gösterilir'}
                            </strong>
                        </span>
                    </button>

                    {missingCount > 0 && (
                        <button
                            type="button"
                            onClick={onAddAllPanels}
                            className="px-3 py-1 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold inline-flex items-center gap-1.5 transition-colors shadow-sm"
                        >
                            <PlusCircle size={13} />
                            Tümünü Ekle ({missingCount})
                        </button>
                    )}
                </div>

                {/* Panel Listesi */}
                <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
                    <ul className="space-y-2">
                        {allPanelConfigs.map(panel => {
                            const Icon = panel.icon;
                            const isAdded = activeSet.has(panel.key);
                            const isListPanel = LIST_PANEL_KEYS.has(panel.key);
                            const listCount = isListPanel ? (dashboardStats?.[panel.key]?.length ?? 0) : null;

                            return (
                                <li
                                    key={panel.key}
                                    onClick={() => onTogglePanel(panel.key)}
                                    className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer transition-all ${
                                        isAdded
                                            ? 'bg-emerald-50/50 dark:bg-emerald-950/20 border-emerald-200/80 dark:border-emerald-800/60 hover:bg-emerald-50 dark:hover:bg-emerald-950/40'
                                            : 'bg-gray-50 hover:bg-blue-50/60 dark:bg-gray-700/50 dark:hover:bg-gray-700 border-gray-200/70 dark:border-gray-600'
                                    }`}
                                >
                                    <div className="flex items-center gap-2.5 min-w-0 pr-2">
                                        {Icon && <Icon size={18} className={`${panel.colorClass} shrink-0`} />}
                                        <div className="min-w-0">
                                            <span className="text-sm font-semibold text-gray-800 dark:text-gray-100 block truncate">
                                                {panel.label}
                                            </span>
                                            {isListPanel && (
                                                <span className="text-[11px] text-gray-500 dark:text-gray-400">
                                                    Şu an {listCount} kayıt var
                                                    {listCount === 0 && hideEmptyLists ? ' (boş olduğu için gizli)' : ''}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {isAdded ? (
                                        <span className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-600/10 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30 text-xs font-semibold hover:bg-rose-500/15 hover:text-rose-600 dark:hover:text-rose-300 hover:border-rose-500/30 transition-colors">
                                            <Check size={14} />
                                            Aktif
                                        </span>
                                    ) : (
                                        <span className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold transition-colors shadow-sm">
                                            <PlusCircle size={14} />
                                            Ekle
                                        </span>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </div>

                {/* Alt Footer */}
                <div className="px-6 py-3.5 border-t border-gray-100 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-800/90 flex items-center justify-between shrink-0">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                        Toplam <strong className="text-gray-800 dark:text-gray-200">{allPanelConfigs.length - missingCount}</strong> / {allPanelConfigs.length} panel aktif
                    </span>
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold inline-flex items-center gap-1.5 transition-colors shadow-sm"
                    >
                        <Check size={15} />
                        Tamam
                    </button>
                </div>
            </div>
        </div>
    );
};

// Personel listesi kart bileşeni
const EmployeeRollCallList = ({ title, employees, icon: Icon, colorClass, maxItems = 7, onSeeAllClick, renderItemExtra, disableLayout }) => (
    <motion.div
        layout={!disableLayout}
        initial={disableLayout ? false : { opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={disableLayout ? undefined : { opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
        className="bg-white dark:bg-gray-800 p-4 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 relative group h-full min-h-0 flex flex-col overflow-hidden"
    >
        <div className="flex items-center justify-between pb-2.5 mb-2.5 border-b border-gray-100 dark:border-gray-700 shrink-0">
            <h3 className={`text-sm font-bold flex items-center min-w-0 ${colorClass}`}>
                <Icon size={17} className="mr-2 shrink-0" />
                <span className="truncate">{title}</span>
            </h3>
            <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 ml-2 shrink-0">
                {employees ? employees.length : 0}
            </span>
        </div>

        {employees && employees.length === 0 ? (
            <p className="text-xs text-gray-500 dark:text-gray-400 my-auto text-center">Kayıt bulunmuyor.</p>
        ) : (
            <ul className="space-y-2 flex-1 min-h-0 overflow-y-auto pr-1">
                {(employees || []).slice(0, maxItems).map(emp => (
                    <li key={emp.id} className="text-xs text-gray-800 dark:text-gray-200 flex items-center py-1 px-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors">
                        <Icon size={14} className={`mr-2 shrink-0 ${colorClass}`} />
                        <span className="font-semibold truncate">{emp.fullName}</span>
                        {emp.departmentName && <span className="ml-1.5 text-gray-500 dark:text-gray-400 text-[11px] truncate">({emp.departmentName})</span>}
                        {renderItemExtra && renderItemExtra(emp)}
                    </li>
                ))}
            </ul>
        )}
        {employees && employees.length > maxItems && (
            <button
                onClick={onSeeAllClick}
                className="mt-2.5 pt-2 border-t border-gray-100 dark:border-gray-700 w-full text-blue-600 dark:text-blue-400 text-xs font-semibold hover:underline shrink-0 text-center"
            >
                Tümünü Gör (+{employees.length - maxItems} kişi)
            </button>
        )}
    </motion.div>
);

export default function TenantDashboard({ onNavigate }) {
    const { firmCode, isAuthenticated, username, fullName, permissions, roles } = useAuth();
    const permSet = useMemo(() => new Set(permissions || []), [permissions]);
    const isAdmin = useMemo(() => (roles || []).includes('Admin'), [roles]);

    // Yetki Bayrakları (RBAC)
    const canViewDeviceReachability = isAdmin || permSet.has('devices:view_list') || permSet.has('settings:manage');
    const canViewQualityWidget = isAdmin || permSet.has('puantaj_quality:dashboard_widget');
    const canViewPendingApprovals = isAdmin || permSet.has('leave_requests:manage') || permSet.has('leave_requests:view_list');
    const canViewBirthdays = isAdmin || permSet.has('dashboard:view_birthdays');
    const canManagePayrollSettings = isAdmin || permSet.has('settings:manage');
    const canTriggerPayroll = canManagePayrollSettings || permSet.has('payroll:calculate');
    const canViewPayrollStatus = canTriggerPayroll || permSet.has('payroll:view_report') || permSet.has('quality:view') || canViewQualityWidget;

    // Kullanıcı ve firma bazlı izole localStorage anahtarları
    const storageScope = `${firmCode || 'default'}_${username || 'user'}`;
    const panelsStorageKey = `dashboardVisiblePanels_${storageScope}`;
    const sizesStorageKey = `dashboardPanelSizes_${storageScope}`;
    const hideEmptyStorageKey = `dashboardHideEmptyLists_${storageScope}`;

    const [selectedDate, setSelectedDate] = useState(getLocalTodayStr);
    const [dashboardStats, setDashboardStats] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [showBirthdayBanner, setShowBirthdayBanner] = useState(false);
    const [showAnniversaryBanner, setShowAnniversaryBanner] = useState(false);
    const [showWelcomeBanner, setShowWelcomeBanner] = useState(false);

    // Modal state'leri
    const [isPlayerListModalOpen, setIsPlayerListModalOpen] = useState(false);
    const [modalTitle, setModalTitle] = useState('');
    const [modalEmployees, setModalEmployees] = useState([]);
    const [modalRenderItemExtra, setModalRenderItemExtra] = useState(null);

    // Özelleştirme state'leri
    const [isEditMode, setIsEditMode] = useState(false);
    const [isAddPanelModalOpen, setIsAddPanelModalOpen] = useState(false);

    const allPanelConfigs = useMemo(() => [
        { key: 'teamStatus', label: 'Ekip Durumu', icon: Users, colorClass: 'text-indigo-600 dark:text-indigo-400', allowed: true },
        { key: 'qualitySummary', label: 'Puantaj Riskleri', icon: AlertCircle, colorClass: 'text-indigo-600 dark:text-indigo-400', allowed: canViewQualityWidget },
        { key: 'pendingApprovals', label: 'Onay Bekleyenler', icon: ClipboardCheck, colorClass: 'text-amber-600 dark:text-amber-500', allowed: canViewPendingApprovals },
        { key: 'myLeaveSummary', label: 'Benim İzin Özetim', icon: Palmtree, colorClass: 'text-emerald-600 dark:text-emerald-500', allowed: true },
        { key: 'leaveEntitlements', label: 'İzin Hak Edişleri', icon: Gift, colorClass: 'text-blue-500 dark:text-blue-400', allowed: true },
        { key: 'deviceReachability', label: 'Cihaz Erişimleri', icon: Radio, colorClass: 'text-emerald-600 dark:text-emerald-400', allowed: canViewDeviceReachability },
        { key: 'insideEmployees', label: 'İçerdeki Personel', icon: UserCheck, colorClass: 'text-green-600 dark:text-green-400', allowed: true },
        { key: 'lateArrivalsEmployees', label: 'Geç Gelen Personel', icon: Clock, colorClass: 'text-red-600 dark:text-red-400', allowed: true },
        { key: 'onLeaveEmployees', label: 'İzinli Personel (Bu Hafta)', icon: CalendarDays, colorClass: 'text-blue-600 dark:text-blue-400', allowed: true },
        { key: 'absentEmployees', label: 'Gelmeyen Personel', icon: UserX, colorClass: 'text-yellow-600 dark:text-yellow-400', allowed: true },
        { key: 'outsideEmployees', label: 'Dışarıdaki Personel', icon: TrendingUp, colorClass: 'text-purple-600 dark:text-purple-400', allowed: true },
        { key: 'upcomingAnniversariesEmployees', label: 'Yıl Dönümü Yaklaşanlar', icon: Award, colorClass: 'text-pink-600 dark:text-pink-400', allowed: true },
        { key: 'newStartersEmployees', label: 'Yeni Başlayanlar (Son 30 Gün)', icon: UserPlus, colorClass: 'text-teal-600 dark:text-teal-400', allowed: true },
        { key: 'todayBirthdaysEmployees', label: 'Bugün Doğum Günü Olanlar', icon: Gift, colorClass: 'text-rose-500 dark:text-rose-400', allowed: canViewBirthdays },
        { key: 'upcomingBirthdaysEmployees', label: 'Yaklaşan Doğum Günleri', icon: CalendarDays, colorClass: 'text-orange-500 dark:text-orange-400', allowed: canViewBirthdays },
    ], [canViewQualityWidget, canViewPendingApprovals, canViewDeviceReachability, canViewBirthdays]);

    const permittedPanelConfigs = useMemo(
        () => allPanelConfigs.filter(p => p.allowed),
        [allPanelConfigs]
    );

    const [visiblePanelKeys, setVisiblePanelKeys] = useState(null);
    const [panelSizes, setPanelSizes] = useState({});
    const [hideEmptyLists, setHideEmptyLists] = useState(false);

    // Kullanıcı/Firma değiştiğinde ilgili profilin panel ayarlarını yükle (Race condition olmadan)
    useEffect(() => {
        try {
            const scopedSaved = localStorage.getItem(panelsStorageKey);
            if (scopedSaved) {
                setVisiblePanelKeys(JSON.parse(scopedSaved));
            } else {
                const legacySaved = localStorage.getItem('dashboardVisiblePanels');
                if (legacySaved) {
                    const parsed = JSON.parse(legacySaved);
                    setVisiblePanelKeys(parsed);
                    localStorage.setItem(panelsStorageKey, JSON.stringify(parsed));
                } else {
                    const defaultKeys = permittedPanelConfigs.map(p => p.key);
                    setVisiblePanelKeys(defaultKeys);
                    localStorage.setItem(panelsStorageKey, JSON.stringify(defaultKeys));
                }
            }

            const scopedSizes = localStorage.getItem(sizesStorageKey);
            if (scopedSizes) {
                setPanelSizes(JSON.parse(scopedSizes));
            } else {
                const legacySizes = localStorage.getItem('dashboardPanelSizes');
                setPanelSizes(legacySizes ? JSON.parse(legacySizes) : {});
            }

            const scopedHideEmpty = localStorage.getItem(hideEmptyStorageKey);
            setHideEmptyLists(scopedHideEmpty === 'true');
        } catch {
            setVisiblePanelKeys(permittedPanelConfigs.map(p => p.key));
            setPanelSizes({});
            setHideEmptyLists(false);
        }
    }, [panelsStorageKey, sizesStorageKey, hideEmptyStorageKey, permittedPanelConfigs]);

    const updateVisiblePanels = useCallback((updater) => {
        setVisiblePanelKeys(prev => {
            const base = Array.isArray(prev) ? prev : permittedPanelConfigs.map(p => p.key);
            const next = typeof updater === 'function' ? updater(base) : updater;
            try {
                localStorage.setItem(panelsStorageKey, JSON.stringify(next));
            } catch (e) {
                console.error('Failed to save dashboard panels to localStorage', e);
            }
            return next;
        });
    }, [panelsStorageKey, permittedPanelConfigs]);

    const updatePanelSizes = useCallback((updater) => {
        setPanelSizes(prev => {
            const next = typeof updater === 'function' ? updater(prev) : updater;
            try {
                localStorage.setItem(sizesStorageKey, JSON.stringify(next));
            } catch {
                /* ignore */
            }
            return next;
        });
    }, [sizesStorageKey]);

    const handleToggleHideEmptyLists = useCallback(() => {
        setHideEmptyLists(prev => {
            const next = !prev;
            try {
                localStorage.setItem(hideEmptyStorageKey, String(next));
            } catch {
                /* ignore */
            }
            return next;
        });
    }, [hideEmptyStorageKey]);

    const handleResetPanelsToDefault = () => {
        const defaultKeys = permittedPanelConfigs.map(p => p.key);
        updateVisiblePanels(defaultKeys);
        updatePanelSizes({});
    };

    const fetchDashboardStats = useCallback(async (forceRefresh = false, dateOverride = null) => {
        if (!isAuthenticated || !firmCode) {
            if (isAuthenticated && !firmCode) return;
            setError('Giriş yapılmamış veya firma kodu mevcut değil.');
            setIsLoading(false);
            return;
        }

        const targetDateToUse = dateOverride || selectedDate;
        if (forceRefresh) {
            setIsRefreshing(true);
        } else if (!dashboardStats) {
            setIsLoading(true);
        }
        setError(null);

        try {
            const query = new URLSearchParams();
            if (targetDateToUse) query.set('targetDate', targetDateToUse);
            if (forceRefresh) query.set('forceRefresh', 'true');

            const data = await apiClient(`Dashboard/stats?${query.toString()}`, { method: 'GET' });
            setDashboardStats(data);

            const todayDateStr = getLocalTodayStr();

            // Kişisel Doğum Günü Kutlaması Kontrolü
            if (data.isCurrentUserBirthday && targetDateToUse === todayDateStr) {
                const storageKey = `birthday_celebrated_${storageScope}_${todayDateStr}`;
                const hasCelebrated = sessionStorage.getItem(storageKey);

                if (!hasCelebrated) {
                    setShowBirthdayBanner(true);
                    sessionStorage.setItem(storageKey, 'true');

                    const duration = 3000;
                    const animationEnd = Date.now() + duration;
                    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };
                    const randomInRange = (min, max) => Math.random() * (max - min) + min;

                    const interval = setInterval(function () {
                        const timeLeft = animationEnd - Date.now();
                        if (timeLeft <= 0) {
                            return clearInterval(interval);
                        }
                        const particleCount = 50 * (timeLeft / duration);
                        confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } });
                        confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } });
                    }, 250);
                }
            }

            // Kişisel İşe Giriş Yıl Dönümü Kontrolü
            if (data.isCurrentUserWorkAnniversary && targetDateToUse === todayDateStr) {
                const storageKey = `work_anniversary_seen_${storageScope}_${todayDateStr}`;
                if (!localStorage.getItem(storageKey)) {
                    setShowAnniversaryBanner(true);
                }
            }

            // Yeni Başlayan Hoş Geldin Kontrolü
            if (data.isCurrentUserNewStarter && targetDateToUse === todayDateStr) {
                const storageKey = `new_starter_welcome_seen_${storageScope}_${todayDateStr}`;
                if (!localStorage.getItem(storageKey)) {
                    setShowWelcomeBanner(true);
                }
            }
        } catch (err) {
            setError(err.message || 'Dashboard verileri yüklenirken bir hata oluştu.');
            console.error('Dashboard API Hatası:', err);
        } finally {
            setIsLoading(false);
            setIsRefreshing(false);
        }
    }, [isAuthenticated, firmCode, selectedDate, storageScope]);

    useEffect(() => {
        fetchDashboardStats(false, selectedDate);
        const intervalId = setInterval(() => {
            if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
            fetchDashboardStats(false, selectedDate);
        }, 5 * 60 * 1000);
        return () => clearInterval(intervalId);
    }, [firmCode, isAuthenticated, selectedDate, fetchDashboardStats]);

    const getStatusBadgeClass = (statusLabel) => {
        if (!statusLabel) return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
        if (statusLabel === 'İçeride') return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300';
        if (statusLabel === 'Dışarıda') return 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300';
        if (statusLabel === 'İzinli') return 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300';
        if (statusLabel === 'Gelmeyen') return 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300';
        if (statusLabel === 'Hafta Tatili' || statusLabel === 'Resmi Tatil') return 'bg-teal-100 text-teal-700 dark:bg-teal-900/50 dark:text-teal-300';
        return 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300';
    };

    const handleOpenPlayerListModal = (category, title) => {
        if (!dashboardStats) return;
        let employeesToList = [];
        let itemExtraRenderer = null;

        switch (category) {
            case 'insideEmployees':
                employeesToList = dashboardStats.insideEmployees || [];
                itemExtraRenderer = (emp) => emp.lastRecordTime ? (
                    <span className="ml-auto text-emerald-600 dark:text-emerald-400 text-xs font-semibold">
                        Son Giriş: {new Date(emp.lastRecordTime).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                ) : null;
                break;
            case 'outsideEmployees':
                employeesToList = dashboardStats.outsideEmployees || [];
                itemExtraRenderer = (emp) => emp.lastRecordTime ? (
                    <span className="ml-auto text-purple-600 dark:text-purple-400 text-xs font-semibold">
                        Son Çıkış: {new Date(emp.lastRecordTime).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                ) : null;
                break;
            case 'presentEmployees':
                employeesToList = [
                    ...(dashboardStats.insideEmployees || []),
                    ...(dashboardStats.outsideEmployees || [])
                ].sort((a, b) => (a.fullName || '').localeCompare(b.fullName || '', 'tr'));
                itemExtraRenderer = (emp) => (
                    <div className="ml-auto flex items-center gap-2">
                        {emp.lastRecordTime && (
                            <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                                {new Date(emp.lastRecordTime).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                        )}
                        {emp.statusLabel && (
                            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${getStatusBadgeClass(emp.statusLabel)}`}>
                                {emp.statusLabel}
                            </span>
                        )}
                    </div>
                );
                break;
            case 'lateArrivalsEmployees':
                employeesToList = dashboardStats.lateArrivalsEmployees || [];
                itemExtraRenderer = (emp) => emp.entryTime ? (
                    <span className="ml-auto text-red-500 dark:text-red-400 text-xs font-semibold">
                        Giriş: {String(emp.entryTime).slice(0, 5)}
                    </span>
                ) : null;
                break;
            case 'onLeaveEmployees':
                employeesToList = dashboardStats.onLeaveEmployees || [];
                itemExtraRenderer = () => (
                    <span className="ml-auto px-2 py-0.5 rounded text-xs font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300">
                        İzinli
                    </span>
                );
                break;
            case 'absentEmployees':
                employeesToList = dashboardStats.absentEmployees || [];
                itemExtraRenderer = (emp) => (
                    <div className="ml-auto flex items-center gap-2">
                        {emp.shiftLabel && (
                            <span className="px-2 py-0.5 rounded text-[11px] bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                                {emp.shiftLabel}
                            </span>
                        )}
                        <span className="px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300">
                            Giriş Yok
                        </span>
                    </div>
                );
                break;
            case 'upcomingAnniversariesEmployees':
                employeesToList = dashboardStats.upcomingAnniversariesEmployees || [];
                itemExtraRenderer = (emp) => emp.yearsAtCompany ? (
                    <span className="ml-auto text-purple-500 dark:text-purple-400 text-xs font-semibold">
                        ({emp.yearsAtCompany}. Yıl)
                    </span>
                ) : null;
                break;
            case 'newStartersEmployees':
                employeesToList = dashboardStats.newStartersEmployees || [];
                itemExtraRenderer = (emp) => emp.hireDate ? (
                    <span className="ml-auto text-blue-500 dark:text-blue-400 text-xs font-medium">
                        (Başlangıç: {new Date(emp.hireDate).toLocaleDateString('tr-TR')})
                    </span>
                ) : null;
                break;
            case 'todayBirthdaysEmployees':
                employeesToList = dashboardStats.todayBirthdaysEmployees || [];
                itemExtraRenderer = (emp) => (
                    <span className="ml-auto text-rose-500 dark:text-rose-400 text-xs font-semibold">
                        (Yaş: {emp.turningAge}){emp.birthdayDisplayLabel ? ` - ${emp.birthdayDisplayLabel}` : ''}
                    </span>
                );
                break;
            case 'upcomingBirthdaysEmployees':
                employeesToList = dashboardStats.upcomingBirthdaysEmployees || [];
                itemExtraRenderer = (emp) => (
                    <span className="ml-auto text-orange-500 dark:text-orange-400 text-xs font-semibold">
                        ({emp.daysUntilBirthday} gün sonra, Yaş: {emp.turningAge}){emp.birthdayDisplayLabel ? ` - ${emp.birthdayDisplayLabel}` : ''}
                    </span>
                );
                break;
            case 'totalEmployees':
                if (dashboardStats.allEmployees && dashboardStats.allEmployees.length > 0) {
                    employeesToList = dashboardStats.allEmployees;
                } else {
                    const allEmployeesSet = new Set();
                    employeesToList = [
                        ...(dashboardStats.insideEmployees || []),
                        ...(dashboardStats.outsideEmployees || []),
                        ...(dashboardStats.lateArrivalsEmployees || []),
                        ...(dashboardStats.onLeaveEmployees || []),
                        ...(dashboardStats.absentEmployees || []),
                        ...(dashboardStats.upcomingAnniversariesEmployees || []),
                        ...(dashboardStats.newStartersEmployees || [])
                    ].filter(emp => {
                        const isNew = !allEmployeesSet.has(emp.id);
                        allEmployeesSet.add(emp.id);
                        return isNew;
                    });
                    employeesToList.sort((a, b) => (a.fullName || '').localeCompare(b.fullName || '', 'tr'));
                }
                itemExtraRenderer = (emp) => (
                    <div className="ml-auto flex items-center gap-2">
                        {emp.shiftLabel && emp.shiftLabel !== emp.statusLabel && (
                            <span className="px-2 py-0.5 rounded text-[11px] bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                                {emp.shiftLabel}
                            </span>
                        )}
                        {emp.statusLabel && (
                            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${getStatusBadgeClass(emp.statusLabel)}`}>
                                {emp.statusLabel}
                            </span>
                        )}
                    </div>
                );
                break;
            default:
                employeesToList = [];
        }

        setModalTitle(title);
        setModalEmployees(employeesToList);
        setModalRenderItemExtra(() => itemExtraRenderer);
        setIsPlayerListModalOpen(true);
    };

    const handleClosePlayerListModal = () => {
        setIsPlayerListModalOpen(false);
        setModalTitle('');
        setModalEmployees([]);
        setModalRenderItemExtra(null);
    };

    const handleRemovePanel = (keyToRemove) => {
        updateVisiblePanels(prev => (prev || []).filter(key => key !== keyToRemove));
    };

    const handleTogglePanel = (keyToToggle) => {
        updateVisiblePanels(prev => {
            const current = prev || [];
            if (current.includes(keyToToggle)) {
                return current.filter(key => key !== keyToToggle);
            }
            return [...current, keyToToggle];
        });
    };

    const handleAddAllPanels = () => {
        updateVisiblePanels(prev => {
            const current = prev || [];
            const missingKeys = permittedPanelConfigs
                .map(p => p.key)
                .filter(k => !current.includes(k));
            return [...current, ...missingKeys];
        });
    };

    const handleCloseAnniversaryBanner = () => {
        const todayDateStr = getLocalTodayStr();
        localStorage.setItem(`work_anniversary_seen_${storageScope}_${todayDateStr}`, 'true');
        setShowAnniversaryBanner(false);
    };

    const handleCloseWelcomeBanner = () => {
        const todayDateStr = getLocalTodayStr();
        localStorage.setItem(`new_starter_welcome_seen_${storageScope}_${todayDateStr}`, 'true');
        setShowWelcomeBanner(false);
    };

    if (isLoading && !dashboardStats) {
        return (
            <div className="flex flex-col items-center justify-center p-8 bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full h-full border border-gray-200 dark:border-gray-700 transition-colors duration-300">
                <Loader2 className="w-12 h-12 text-blue-500 dark:text-blue-400 animate-spin mb-4" />
                <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">Anasayfa Verileri Yükleniyor...</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Lütfen bekleyiniz.</p>
            </div>
        );
    }

    if (error && !dashboardStats) {
        return (
            <div className="flex flex-col items-center justify-center p-8 bg-red-50 dark:bg-red-900/30 rounded-2xl shadow-xl w-full h-full border border-red-200 dark:border-red-800 text-red-700 dark:text-red-100 transition-colors duration-300">
                <AlertCircle className="w-14 h-14 mb-4 text-red-500" />
                <h2 className="text-xl font-bold mb-2">Veriler Yüklenemedi</h2>
                <p className="text-sm text-center max-w-md">{error}</p>
                <button
                    onClick={() => fetchDashboardStats(true)}
                    className="mt-4 px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-xl shadow hover:bg-blue-700 transition-all inline-flex items-center gap-2"
                >
                    <RefreshCw size={16} /> Tekrar Dene
                </button>
            </div>
        );
    }

    const getPanelSize = (key) => panelSizes[key] || getDefaultPanelSize(key);
    const getPanelSpanClass = (key) => panelSizeToClass(getPanelSize(key));

    const orderedVisibleKeys = (visiblePanelKeys || []).filter(key =>
        permittedPanelConfigs.some(p => p.key === key)
    );

    const viewPanelKeys = orderedVisibleKeys.filter(key => {
        if (!hideEmptyLists || !LIST_PANEL_KEYS.has(key)) return true;
        const employees = dashboardStats?.[key];
        return employees && employees.length > 0;
    });

    const renderEmployeeExtras = (panelKey) => (emp) => {
        switch (panelKey) {
            case 'insideEmployees':
            case 'outsideEmployees':
                return emp.lastRecordTime ? (
                    <span className="ml-auto text-gray-500 dark:text-gray-400 text-[11px] font-medium">
                        {new Date(emp.lastRecordTime).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                ) : null;
            case 'lateArrivalsEmployees':
                return emp.entryTime ? (
                    <span className="ml-auto text-red-500 dark:text-red-400 text-[11px] font-semibold">
                        {String(emp.entryTime).slice(0, 5)}
                    </span>
                ) : null;
            case 'upcomingAnniversariesEmployees':
                return emp.yearsAtCompany ? (
                    <span className="ml-auto text-purple-500 dark:text-purple-400 text-[11px] font-semibold">
                        {emp.yearsAtCompany}. yıl
                    </span>
                ) : null;
            case 'newStartersEmployees':
                return emp.hireDate ? (
                    <span className="ml-auto text-blue-500 dark:text-blue-400 text-[11px]">
                        {new Date(emp.hireDate).toLocaleDateString('tr-TR')}
                    </span>
                ) : null;
            case 'todayBirthdaysEmployees':
                return (
                    <span className="ml-auto text-rose-500 dark:text-rose-400 text-[11px] font-semibold">
                        Yaş: {emp.turningAge}{emp.birthdayDisplayLabel ? ` • ${emp.birthdayDisplayLabel}` : ''}
                    </span>
                );
            case 'upcomingBirthdaysEmployees':
                return (
                    <span className="ml-auto text-orange-500 dark:text-orange-400 text-[11px] font-semibold">
                        {emp.daysUntilBirthday}g ({emp.turningAge})
                    </span>
                );
            default:
                return null;
        }
    };

    const renderPanelContent = (key, { isEditing } = {}) => {
        if (key === 'deviceReachability') {
            return <DeviceReachabilityCard onNavigate={isEditing ? undefined : onNavigate} isEditMode={!!isEditing} />;
        }
        if (key === 'qualitySummary') {
            return (
                <QualityDashboardWidget
                    isEditMode={!!isEditing}
                    onActionComplete={(force = true) => fetchDashboardStats(force)}
                />
            );
        }
        if (key === 'teamStatus') {
            return dashboardStats?.teamStatus ? (
                <TeamStatusWidget
                    teamStatus={dashboardStats.teamStatus}
                    isEditMode={!!isEditing}
                    onOpenEmployeeModal={isEditing ? undefined : handleOpenPlayerListModal}
                />
            ) : (
                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-4 text-sm text-gray-500">
                    Ekip Durumu
                </div>
            );
        }
        if (key === 'pendingApprovals') {
            return (
                <PendingApprovalWidget
                    pendingApprovalsCount={dashboardStats?.pendingApprovalsCount}
                    pendingApprovalsPreviewItems={dashboardStats?.pendingApprovalsPreviewItems}
                    isEditMode={!!isEditing}
                    onActionComplete={(force = true) => fetchDashboardStats(force)}
                />
            );
        }
        if (key === 'myLeaveSummary') {
            return <MyLeaveSummaryWidget stats={dashboardStats} loading={false} onNavigate={isEditing ? undefined : onNavigate} />;
        }
        if (key === 'leaveEntitlements') {
            return <LeaveEntitlementWidget stats={dashboardStats} loading={false} onNavigate={isEditing ? undefined : onNavigate} />;
        }

        const panel = permittedPanelConfigs.find(p => p.key === key);
        if (!panel) return null;
        const employees = dashboardStats?.[panel.key] || [];
        return (
            <EmployeeRollCallList
                title={panel.label}
                employees={employees}
                icon={panel.icon}
                colorClass={panel.colorClass}
                maxItems={7}
                onSeeAllClick={() => handleOpenPlayerListModal(panel.key, panel.label)}
                renderItemExtra={renderEmployeeExtras(panel.key)}
                disableLayout={!!isEditing}
            />
        );
    };

    const sortableItems = orderedVisibleKeys.map(key => {
        const cfg = permittedPanelConfigs.find(p => p.key === key);
        return {
            key,
            label: cfg?.label || key,
            icon: cfg?.icon,
            spanClass: getPanelSpanClass(key),
            size: getPanelSize(key)
        };
    });

    const todayStr = getLocalTodayStr();
    const yesterdayStr = getLocalYesterdayStr();
    const missingPanelCount = permittedPanelConfigs.length - orderedVisibleKeys.length;

    return (
        <div className={`p-4 sm:p-6 space-y-6 min-h-full ${isEditMode ? 'bg-transparent' : 'bg-gray-100 dark:bg-gray-900'}`}>
            {isEditMode && (
                <>
                    <div className="fixed inset-0 z-40 bg-slate-950/70 backdrop-blur-sm" />
                    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[80] w-[min(96vw,900px)]">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 rounded-2xl bg-slate-900 text-white px-5 py-3.5 shadow-2xl border border-white/10">
                            <div>
                                <p className="font-semibold text-sm">Anasayfa Panellerini Düzenle</p>
                                <p className="text-xs text-slate-300">Kartları sürükleyerek sıralayın, sağ alt köşeden boyutlandırın.</p>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                                <button
                                    type="button"
                                    onClick={handleToggleHideEmptyLists}
                                    className={`px-3 py-2 rounded-lg text-xs font-medium inline-flex items-center transition-colors ${
                                        hideEmptyLists
                                            ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30 hover:bg-amber-500/30'
                                            : 'bg-white/10 text-slate-200 hover:bg-white/20'
                                    }`}
                                    title="Boş personel listelerini normal görünümde gizle / göster"
                                >
                                    {hideEmptyLists ? <EyeOff size={14} className="mr-1.5" /> : <Eye size={14} className="mr-1.5" />}
                                    {hideEmptyLists ? 'Boş Listeler Gizli' : 'Boş Listeler Görünür'}
                                </button>
                                <button
                                    type="button"
                                    onClick={handleResetPanelsToDefault}
                                    className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-xs font-medium inline-flex items-center transition-colors"
                                    title="Varsayılan yerleşime sıfırla"
                                >
                                    <RotateCcw size={14} className="mr-1.5" /> Sıfırla
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setIsAddPanelModalOpen(true)}
                                    className="px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-xs font-semibold inline-flex items-center transition-colors"
                                >
                                    <PlusCircle size={15} className="mr-1.5" />
                                    Panel Ekle / Yönet
                                    {missingPanelCount > 0 && (
                                        <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-white/20 text-[10px] font-bold">
                                            +{missingPanelCount}
                                        </span>
                                    )}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setIsEditMode(false)}
                                    className="px-3.5 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-xs font-bold inline-flex items-center transition-colors"
                                >
                                    <Check size={15} className="mr-1.5" /> Kaydet & Bitir
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {dashboardStats && (
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35 }}
                    className={`space-y-6 ${isEditMode ? 'relative z-50 pt-20' : ''}`}
                >
                    {/* Üst Kontrol ve Tarih Seçim Barı */}
                    {!isEditMode && (
                        <div className="bg-white dark:bg-gray-800 rounded-2xl px-4 py-3 shadow-sm border border-gray-200/80 dark:border-gray-700/80 flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
                            {/* Sol: Tarih Filtresi ve Hızlı Geçiş */}
                            <div className="flex flex-wrap items-center gap-2">
                                <div className="inline-flex rounded-xl bg-gray-100 dark:bg-gray-700/70 p-1">
                                    <button
                                        type="button"
                                        onClick={() => setSelectedDate(todayStr)}
                                        className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
                                            selectedDate === todayStr
                                                ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-sm'
                                                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                                        }`}
                                    >
                                        Bugün
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setSelectedDate(yesterdayStr)}
                                        className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
                                            selectedDate === yesterdayStr
                                                ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-sm'
                                                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                                        }`}
                                    >
                                        Dün
                                    </button>
                                </div>

                                <div className="relative flex items-center">
                                    <Calendar size={14} className="absolute left-3 text-gray-400 pointer-events-none" />
                                    <input
                                        type="date"
                                        value={selectedDate}
                                        max={todayStr}
                                        onChange={(e) => {
                                            if (e.target.value) setSelectedDate(e.target.value);
                                        }}
                                        className="pl-8 pr-3 py-1.5 rounded-xl text-xs font-medium bg-gray-50 dark:bg-gray-700/60 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                                    />
                                </div>
                            </div>

                            {/* Sağ: Son Güncelleme, Yenile ve Panelleri Düzenle */}
                            <div className="flex flex-wrap items-center gap-2.5 w-full md:w-auto justify-between md:justify-end">
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                    Son güncelleme:{' '}
                                    <strong className="text-gray-700 dark:text-gray-200">
                                        {new Date(dashboardStats.lastCalculatedTime).toLocaleTimeString('tr-TR')}
                                    </strong>
                                </span>

                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => fetchDashboardStats(true)}
                                        disabled={isRefreshing}
                                        className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-xl text-xs font-semibold flex items-center transition-colors disabled:opacity-50"
                                        title="Önbelleği temizleyip verileri şimdi yenile"
                                    >
                                        <RefreshCw size={14} className={`mr-1.5 ${isRefreshing ? 'animate-spin text-blue-500' : ''}`} />
                                        Yenile
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => setIsEditMode(true)}
                                        className="px-3.5 py-1.5 bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-900/40 dark:hover:bg-indigo-900/60 text-indigo-700 dark:text-indigo-300 border border-indigo-200/60 dark:border-indigo-800/60 rounded-xl text-xs font-semibold flex items-center transition-colors"
                                    >
                                        <Settings size={14} className="mr-1.5" />
                                        Panelleri Düzenle
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {!isEditMode && (
                        <>
                            {/* Kişisel Doğum Günü Kutlama Banner'ı */}
                            {showBirthdayBanner && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.95, y: -20 }}
                                    animate={{ opacity: 1, scale: 1, y: 0 }}
                                    exit={{ opacity: 0, scale: 0.95, y: -20 }}
                                    className="bg-gradient-to-r from-pink-500 to-rose-400 rounded-2xl p-4 shadow-lg text-white flex justify-between items-center relative overflow-hidden"
                                >
                                    <div className="flex items-center z-10">
                                        <Gift className="w-10 h-10 mr-4 text-white opacity-90" />
                                        <div>
                                            <h3 className="text-xl font-bold">İyi ki doğdun{fullName || username ? `, ${fullName || username}` : ''}! 🎉</h3>
                                            <p className="text-pink-100 text-sm mt-1">
                                                Bugün senin günün, mutlu yıllar dileriz.
                                                {dashboardStats.currentUserTurningAge && ` (Bugün ${dashboardStats.currentUserTurningAge} yaşına girdin!)`}
                                                {dashboardStats.currentUserBirthdayLabel && ` - ${dashboardStats.currentUserBirthdayLabel}`}
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setShowBirthdayBanner(false)}
                                        className="z-10 text-white hover:text-pink-200 transition-colors bg-white/20 hover:bg-white/30 rounded-full p-2"
                                        title="Kapat"
                                    >
                                        <X size={20} />
                                    </button>
                                </motion.div>
                            )}

                            {/* İşe Giriş Yıl Dönümü Banner'ı */}
                            {showAnniversaryBanner && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.95, y: -20 }}
                                    animate={{ opacity: 1, scale: 1, y: 0 }}
                                    exit={{ opacity: 0, scale: 0.95, y: -20 }}
                                    className="bg-gradient-to-r from-indigo-500 to-blue-500 rounded-2xl p-4 shadow-lg text-white flex justify-between items-center relative overflow-hidden"
                                >
                                    <div className="flex items-center z-10">
                                        <Award className="w-10 h-10 mr-4 text-white opacity-90" />
                                        <div>
                                            <h3 className="text-xl font-bold">Bugün şirkette {dashboardStats.currentUserCompletedWorkYears}. yılın 🎉</h3>
                                            <p className="text-indigo-100 text-sm mt-1">
                                                Emeklerin için teşekkür ederiz.
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={handleCloseAnniversaryBanner}
                                        className="z-10 text-white hover:text-indigo-200 transition-colors bg-white/20 hover:bg-white/30 rounded-full p-2"
                                        title="Kapat"
                                    >
                                        <X size={20} />
                                    </button>
                                </motion.div>
                            )}

                            {/* Yeni Başlayan Hoş Geldin Banner'ı */}
                            {showWelcomeBanner && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.95, y: -20 }}
                                    animate={{ opacity: 1, scale: 1, y: 0 }}
                                    exit={{ opacity: 0, scale: 0.95, y: -20 }}
                                    className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-lg border border-teal-200 dark:border-teal-800 flex justify-between items-center relative overflow-hidden"
                                >
                                    <div className="flex items-center z-10">
                                        <span className="text-4xl mr-4" role="img" aria-label="Waving Hand">👋</span>
                                        <div>
                                            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100">Aramıza hoş geldin</h3>
                                            <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
                                                İlk haftan hayırlı olsun. {dashboardStats.currentUserDaysSinceStart > 0 && `(Bugün ${dashboardStats.currentUserDaysSinceStart + 1}. günün)`}
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={handleCloseWelcomeBanner}
                                        className="z-10 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full p-2"
                                        title="Kapat"
                                    >
                                        <X size={20} />
                                    </button>
                                    <div className="absolute left-0 top-0 bottom-0 w-2 bg-teal-500 rounded-l-2xl"></div>
                                </motion.div>
                            )}

                            {/* Özel Günler Banner - En Üstte */}
                            <div className="w-full -mb-2">
                                <DashboardSpecialDayBanner />
                            </div>

                            {/* Duyurular - En Üstte ve Kapatılamaz */}
                            <div className="w-full">
                                <AnnouncementsWidget />
                            </div>

                            {/* Dairesel İstatistik Kartları */}
                            <CircularStatsPanel
                                totalEmployees={dashboardStats.totalEmployees}
                                insideEmployeesCount={dashboardStats.insideEmployeesCount}
                                outsideEmployeesCount={dashboardStats.outsideEmployeesCount}
                                lateArrivalsCount={dashboardStats.lateArrivalsCount}
                                onLeaveCount={dashboardStats.onLeaveCount}
                                absentCount={dashboardStats.absentCount}
                                upcomingAnniversariesCount={dashboardStats.upcomingAnniversariesCount}
                                newStartersCount={dashboardStats.newStartersCount}
                                onCardClick={handleOpenPlayerListModal}
                            />

                            {/* Puantaj Hesaplama Motoru Durum Kartı (Sadece yetkili kullanıcılara gösterilir) */}
                            {canViewPayrollStatus && (
                                <PayrollStatusCard
                                    onNavigate={onNavigate}
                                    canTrigger={canTriggerPayroll}
                                    canManageSettings={canManagePayrollSettings}
                                />
                            )}
                        </>
                    )}

                    {isEditMode ? (
                        <DashboardSortableGrid
                            items={sortableItems}
                            onReorder={(nextOrdered) => {
                                updateVisiblePanels(prev => {
                                    const hidden = (prev || []).filter(k => !nextOrdered.includes(k));
                                    return [...nextOrdered, ...hidden];
                                });
                            }}
                            onRemove={handleRemovePanel}
                            onResize={(key, nextSize) => {
                                updatePanelSizes(prev => {
                                    const current = prev[key] || getDefaultPanelSize(key);
                                    if (current.cols === nextSize.cols && current.rows === nextSize.rows) return prev;
                                    return { ...prev, [key]: nextSize };
                                });
                            }}
                            renderItem={(key) => renderPanelContent(key, { isEditing: true })}
                        />
                    ) : (
                        <div className={DASHBOARD_PANEL_GRID_CLASS}>
                            {viewPanelKeys.map(key => (
                                <div
                                    key={key}
                                    className={`min-w-0 min-h-0 h-full ${getPanelSpanClass(key)} [&>*]:h-full [&>*]:min-h-0 [&>*]:overflow-hidden`}
                                >
                                    {renderPanelContent(key)}
                                </div>
                            ))}
                        </div>
                    )}
                </motion.div>
            )}

            {/* Personel Listesi Modalı */}
            <PlayerListModal
                isOpen={isPlayerListModalOpen}
                onClose={handleClosePlayerListModal}
                title={modalTitle}
                employees={modalEmployees}
                isLoading={isLoading}
                error={error}
                renderItemExtra={modalRenderItemExtra}
            />

            {/* Panel Ekleme ve Yönetme Modalı */}
            <AddPanelModal
                isOpen={isAddPanelModalOpen}
                onClose={() => setIsAddPanelModalOpen(false)}
                allPanelConfigs={permittedPanelConfigs}
                visiblePanelKeys={orderedVisibleKeys}
                onTogglePanel={handleTogglePanel}
                onAddAllPanels={handleAddAllPanels}
                dashboardStats={dashboardStats}
                hideEmptyLists={hideEmptyLists}
                onToggleHideEmptyLists={handleToggleHideEmptyLists}
            />
        </div>
    );
}
